import { DqIcon } from "@decentriq/components";
import { faCheck, faExclamationCircle } from "@fortawesome/pro-light-svg-icons";
import {
  Button,
  Chip,
  type ChipProps,
  Stack,
  Tooltip,
  Typography,
} from "@mui/joy";
import { memo, useMemo } from "react";
import { DataRoomErrorReport } from "components";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts";
import {
  type AudienceAb,
  type AudienceStatus,
} from "features/mediaDataRoom/models";
import { replaceDecentriqOrgName } from "features/mediaDataRoom/utils";
import { useAudiences } from "features/mediaDataRoom/wrappers";
import { mapMediaDataRoomErrorToSnackbar } from "hooks";

const audienceStatusPresentationMap = new Map<AudienceStatus, string>([
  ["published", "Available to publisher"],
  ["ready", "Ready"],
]);

type AudienceAbStatusChipProps = AudienceAb;

const AudienceAbStatusChip = memo<AudienceAbStatusChipProps>(
  ({ id, status: originalStatus, isPublishedAsIntermediate }) => {
    const { baseAudienceProviderName } = useMediaDataRoom();
    const {
      audienceSizes: { audienceSizes, retrySizeEstimationForAudience },
    } = useAudiences();
    const sizeState = audienceSizes[id];
    const error = useMemo<
      { message: string | undefined; details?: string } | undefined
    >(() => {
      if (sizeState?.error) {
        const [message, details] = mapMediaDataRoomErrorToSnackbar(
          sizeState.error,
          "Unable to estimate audience size"
        );
        return {
          details: details?.context,
          message,
        };
      }
    }, [sizeState?.error]);
    const hasError = useMemo(() => Boolean(error?.message), [error?.message]);
    const tooltipTitle = useMemo<string | undefined>(
      () => (hasError ? error?.message : undefined),
      [hasError, error]
    );
    const hasTooltip = Boolean(tooltipTitle?.length);
    const hasErrorActions = Boolean(sizeState?.error);
    const isComputingSize = sizeState?.loading;
    const label = useMemo(() => {
      return `Available to ${replaceDecentriqOrgName(baseAudienceProviderName, "Publisher")}`;
    }, [baseAudienceProviderName]);
    const status: AudienceStatus =
      originalStatus === "published" && isPublishedAsIntermediate
        ? "ready"
        : originalStatus;
    const color = useMemo<ChipProps["color"]>(
      () => (hasError ? "danger" : isComputingSize ? "neutral" : "success"),
      [hasError, isComputingSize]
    );
    return (
      <DataRoomErrorReport
        details={error?.details ?? ""}
        error={error?.message ?? ""}
        renderTrigger={(openErrorReportDialog) => (
          <Tooltip
            title={
              hasTooltip ? (
                <Stack direction="column">
                  <Typography level="body-sm" sx={{ color: "inherit" }}>
                    {tooltipTitle}
                  </Typography>
                  {hasErrorActions && (
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      spacing={1}
                    >
                      <Button
                        onClick={() => retrySizeEstimationForAudience(id)}
                        size="sm"
                        variant="soft"
                      >
                        Retry
                      </Button>
                      <Button
                        color="danger"
                        onClick={openErrorReportDialog}
                        size="sm"
                        variant="solid"
                      >
                        Report issue to Decentriq
                      </Button>
                    </Stack>
                  )}
                </Stack>
              ) : undefined
            }
          >
            <Chip
              color={color}
              component="span"
              size="md"
              startDecorator={
                <DqIcon
                  icon={
                    isComputingSize
                      ? undefined
                      : hasError
                        ? faExclamationCircle
                        : faCheck
                  }
                />
              }
              sx={(theme) => ({
                borderRadius: theme.radius.sm,
              })}
            >
              {hasError
                ? "Error"
                : isComputingSize
                  ? "Computing"
                  : status === "published"
                    ? label
                    : audienceStatusPresentationMap.get(status)}
            </Chip>
          </Tooltip>
        )}
      />
    );
  }
);
AudienceAbStatusChip.displayName = "AudienceAbStatusChip";

export default AudienceAbStatusChip;
